import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MapPin, RefreshCw, ShoppingBag } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { MerchPerformancePanel } from '@/components/merch/MerchPerformancePanel'
import {
  CatalogProductForm,
  blankCatalogForm,
  type CatalogItemForm,
} from '@/components/merch/CatalogProductForm'

interface MerchVariant {
  id: string
  label: string
  sku?: string | null
  inventory: number
  reserved: number
  pricePence?: number | null
}

interface CatalogItem {
  id: string
  name: string
  sku: string
  pricePence: number
  description?: string | null
  status: 'draft' | 'active' | 'archived'
  tags: string[]
  images: Array<{ url?: string; alt?: string; position?: number }>
  madeToOrder: boolean
  minTierLevel: number | null
  shopifyProductId?: string | null
  wooProductId?: string | null
  lastSyncedAt?: string | null
  syncError?: string | null
  variants: MerchVariant[]
}

interface CatalogSettings {
  catalogEditOrigin: 'ovation' | 'shopify' | 'woocommerce'
  shopifyShopDomain?: string | null
  wooSiteUrl?: string | null
}

/** Deep-link into the configured source-of-truth admin for a catalogue product. */
function sourceOfTruthEditUrl(
  origin: CatalogSettings['catalogEditOrigin'],
  settings: CatalogSettings | undefined,
  item: CatalogItem,
): string | null {
  if (origin === 'shopify') {
    const domain = settings?.shopifyShopDomain?.replace(/^https?:\/\//, '').replace(/\/$/, '')
    if (!domain) return null
    const handle = domain.replace(/\.myshopify\.com$/i, '')
    if (item.shopifyProductId) {
      return `https://admin.shopify.com/store/${handle}/products/${item.shopifyProductId}`
    }
    return `https://admin.shopify.com/store/${handle}/products`
  }
  if (origin === 'woocommerce') {
    const site = settings?.wooSiteUrl?.replace(/\/$/, '')
    if (!site) return null
    if (item.wooProductId) {
      return `${site}/wp-admin/post.php?post=${item.wooProductId}&action=edit`
    }
    return `${site}/wp-admin/edit.php?post_type=product`
  }
  return null
}

interface StoreSummary {
  totalOrders: number
  totalRevenuePence: number
  revenueByCountry: Array<{ countryCode: string; count: number; revenuePence: number }>
  revenueByRegion: Array<{ regionKey: string; label: string; count: number; revenuePence: number }>
  revenueBySource: Array<{ source: string; count: number; revenuePence: number }>
}

interface ProductMixRow {
  category: string
  label: string
  quantity: number
  revenuePence: number
  share: number
}

interface MerchInsight {
  type: 'product_preference' | 'promote' | 'channel'
  regionLabel: string
  message: string
}

interface MerchStoreIntelligence {
  ordersAnalyzed: number
  itemsAnalyzed: number
  shipFrom: Array<{ source: string; label: string; orders: number; revenuePence: number }>
  globalProductMix: ProductMixRow[]
  shipTo: Array<{
    locationKey: string
    label: string
    geographicLevel: 'country' | 'state' | 'city'
    orders: number
    revenuePence: number
    productMix: ProductMixRow[]
    leadCategory: string | null
  }>
  insights: MerchInsight[]
}

interface StoreOrder {
  id: string
  source: string
  externalOrderId: string
  orderNumber: string | null
  totalPence: number
  currency: string
  city: string | null
  countryCode: string | null
  region: string | null
  orderedAt: string
  lineItems: Array<{ title?: string; quantity?: number }> | unknown
}

function formatMoney(pence: number, currency = 'GBP'): string {
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£'
  return `${symbol}${(pence / 100).toFixed(2)}`
}

function itemToForm(item: CatalogItem): CatalogItemForm {
  const images = Array.isArray(item.images) ? item.images : []
  const variants: CatalogItemForm['variants'] =
    item.variants.length > 0
      ? item.variants.map((v) => {
          const row: CatalogItemForm['variants'][number] = {
            label: v.label,
            inventory: v.inventory,
          }
          if (v.id) row.id = v.id
          if (v.sku) row.sku = v.sku
          if (v.pricePence != null) row.pricePence = v.pricePence
          return row
        })
      : [{ label: 'Default', inventory: 0 }]

  return {
    name: item.name,
    sku: item.sku,
    pricePence: item.pricePence,
    description: item.description ?? '',
    status: item.status,
    tags: (item.tags ?? []).join(', '),
    imageUrl: images[0]?.url ?? '',
    madeToOrder: item.madeToOrder,
    variants,
  }
}

function formToPayload(form: CatalogItemForm) {
  return {
    name: form.name,
    sku: form.sku,
    pricePence: form.pricePence,
    description: form.description || null,
    status: form.status,
    tags: form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean),
    images: form.imageUrl
      ? [{ url: form.imageUrl, position: 0 }]
      : [],
    madeToOrder: form.madeToOrder,
    variants: form.variants.map((v) => ({
      id: v.id,
      label: v.label,
      sku: v.sku || null,
      inventory: v.inventory,
      pricePence: v.pricePence ?? null,
    })),
  }
}

export function MerchPage() {
  const qc = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)

  const catalogQ = useQuery<CatalogItem[], ApiClientError>({
    queryKey: ['merch', 'catalog-items'],
    queryFn: () => artistFetch<CatalogItem[]>('/merch/catalog/items'),
  })

  const settingsQ = useQuery<CatalogSettings, ApiClientError>({
    queryKey: ['merch', 'catalog-settings'],
    queryFn: () => artistFetch<CatalogSettings>('/merch/catalog/settings'),
  })

  const storeSummaryQ = useQuery<StoreSummary, ApiClientError>({
    queryKey: ['merch', 'store-summary'],
    queryFn: () => artistFetch<StoreSummary>('/merch/store/summary'),
  })

  const storeOrdersQ = useQuery<StoreOrder[], ApiClientError>({
    queryKey: ['merch', 'store-orders'],
    queryFn: () => artistFetch<StoreOrder[]>('/merch/store/orders?limit=50'),
  })

  const intelligenceQ = useQuery<MerchStoreIntelligence, ApiClientError>({
    queryKey: ['merch', 'store-intelligence'],
    queryFn: () => artistFetch<MerchStoreIntelligence>('/merch/store/intelligence'),
  })

  const saveM = useMutation({
    mutationFn: async ({ id, form }: { id?: string; form: CatalogItemForm }) => {
      const body = formToPayload(form)
      if (id) {
        return artistFetch(`/merch/catalog/items/${id}`, { method: 'PATCH', body })
      }
      return artistFetch('/merch/catalog/items', { method: 'POST', body })
    },
    onSuccess: async () => {
      setCreating(false)
      setEditingId(null)
      await qc.invalidateQueries({ queryKey: ['merch', 'catalog-items'] })
    },
  })

  const deleteM = useMutation({
    mutationFn: (id: string) =>
      artistFetch(`/merch/catalog/items/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['merch', 'catalog-items'] })
    },
  })

  const originM = useMutation({
    mutationFn: (catalogEditOrigin: CatalogSettings['catalogEditOrigin']) =>
      artistFetch('/merch/catalog/settings', {
        method: 'PATCH',
        body: { catalogEditOrigin },
      }),
    onSuccess: async (_data, catalogEditOrigin) => {
      if (catalogEditOrigin !== 'ovation') {
        setCreating(false)
        setEditingId(null)
      }
      await qc.invalidateQueries({ queryKey: ['merch', 'catalog-settings'] })
    },
  })

  const syncM = useMutation({
    mutationFn: () =>
      artistFetch<{ ok: boolean; result: { pulled: number; upserted: number; pushed: number; errors: string[] } }>(
        '/merch/catalog/sync',
        { method: 'POST' },
      ),
    onSuccess: async (data) => {
      const r = data.result
      setSyncMessage(
        `Synced: pulled ${r.pulled}, upserted ${r.upserted}, pushed ${r.pushed}`
          + (r.errors.length ? ` (${r.errors.length} errors)` : ''),
      )
      await qc.invalidateQueries({ queryKey: ['merch', 'catalog-items'] })
    },
  })

  const items = catalogQ.data ?? []
  const summary = storeSummaryQ.data
  const intel = intelligenceQ.data
  const editingItem = editingId ? items.find((i) => i.id === editingId) : null
  const editOrigin = settingsQ.data?.catalogEditOrigin ?? 'ovation'
  const ovationEditable = editOrigin === 'ovation'
  const originLabel =
    editOrigin === 'shopify'
      ? 'Shopify'
      : editOrigin === 'woocommerce'
        ? 'WooCommerce'
        : 'Ovation'

  return (
    <div className="space-y-8">
      <PageHeader
        title="Merch"
        subtitle="Observe catalogue sync, show pre-orders, and online store sales. Edit products in your chosen source of truth."
      />
      <PerformanceScopeBanner />

      {/* Catalogue SSO */}
      <section className="rounded-xl border border-ovation-ink/10 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl">
              <ShoppingBag className="h-5 w-5" />
              Catalogue
            </h2>
            <p className="mt-1 text-sm text-ovation-muted">
              Ovation syncs and reports. Connect stores on{' '}
              <Link to="/integrations" className="underline">
                Integrations
              </Link>
              , pick a source of truth, then Sync now.
              {!ovationEditable && (
                <>
                  {' '}
                  Catalogue is <strong>read-only</strong> here — edit in {originLabel}.
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={editOrigin}
              onChange={(e) =>
                originM.mutate(e.target.value as CatalogSettings['catalogEditOrigin'])
              }
              className="rounded border border-ovation-ink/15 px-2 py-1.5 text-sm"
            >
              <option value="ovation">Source: Ovation</option>
              <option value="shopify">Source: Shopify</option>
              <option value="woocommerce">Source: WooCommerce</option>
            </select>
            <button
              type="button"
              onClick={() => syncM.mutate()}
              disabled={syncM.isPending}
              className="inline-flex items-center gap-1.5 rounded border border-ovation-ink/15 px-3 py-1.5 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncM.isPending ? 'animate-spin' : ''}`} />
              Sync now
            </button>
            {ovationEditable && (
              <button
                type="button"
                onClick={() => {
                  setCreating(true)
                  setEditingId(null)
                }}
                className="rounded bg-ovation-ink px-3 py-1.5 text-sm text-white"
              >
                Add product
              </button>
            )}
          </div>
        </div>

        {syncMessage && (
          <p className="mt-3 text-sm text-ovation-muted">{syncMessage}</p>
        )}
        {syncM.isError && (
          <p className="mt-3 text-sm text-red-600">
            {(syncM.error as ApiClientError).message}
          </p>
        )}

        {ovationEditable && (creating || editingItem) && (
          <div className="mt-4">
            <CatalogProductForm
              initial={editingItem ? itemToForm(editingItem) : blankCatalogForm()}
              submitting={saveM.isPending}
              onCancel={() => {
                setCreating(false)
                setEditingId(null)
              }}
              onSubmit={(form) => {
                if (editingItem?.id) saveM.mutate({ id: editingItem.id, form })
                else saveM.mutate({ form })
              }}
            />
          </div>
        )}

        <AsyncBoundary isLoading={catalogQ.isLoading} error={catalogQ.error}>
          <div className="mt-4">
            <DataTable
              rows={items}
              rowKey={(r) => r.id}
              emptyMessage={
                ovationEditable
                  ? 'No catalogue products yet. Add one or Sync from Shopify/Woo.'
                  : `No catalogue products yet. Sync from ${originLabel}.`
              }
              columns={[
                { key: 'name', header: 'Product', render: (r) => r.name },
                { key: 'sku', header: 'SKU', render: (r) => r.sku },
                {
                  key: 'price',
                  header: 'Price',
                  align: 'right',
                  render: (r) => formatMoney(r.pricePence),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (r) => r.status,
                },
                {
                  key: 'stock',
                  header: 'Stock',
                  align: 'right',
                  render: (r) =>
                    r.variants.reduce((s, v) => s + v.inventory - v.reserved, 0),
                },
                {
                  key: 'channels',
                  header: 'Channels',
                  render: (r) =>
                    [r.shopifyProductId && 'Shopify', r.wooProductId && 'Woo']
                      .filter(Boolean)
                      .join(', ') || '—',
                },
                {
                  key: 'sync',
                  header: 'Sync',
                  render: (r) =>
                    r.syncError ? (
                      <span className="text-red-600" title={r.syncError}>
                        Error
                      </span>
                    ) : r.lastSyncedAt ? (
                      new Date(r.lastSyncedAt).toLocaleDateString()
                    ) : (
                      '—'
                    ),
                },
                {
                  key: 'actions',
                  header: '',
                  render: (r) => {
                    if (ovationEditable) {
                      return (
                        <div className="flex gap-2 text-xs">
                          <button
                            type="button"
                            className="underline"
                            onClick={() => {
                              setEditingId(r.id)
                              setCreating(false)
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-red-600 underline"
                            onClick={() => {
                              if (confirm(`Archive ${r.name}?`)) deleteM.mutate(r.id)
                            }}
                          >
                            Archive
                          </button>
                        </div>
                      )
                    }

                    const url = sourceOfTruthEditUrl(editOrigin, settingsQ.data, r)
                    if (!url) {
                      return (
                        <span
                          className="text-xs text-ovation-muted"
                          title={`Connect ${originLabel} on Integrations to open the editor`}
                        >
                          Edit in {originLabel}
                        </span>
                      )
                    }
                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline"
                      >
                        Edit in {originLabel}
                      </a>
                    )
                  },
                },
              ]}
            />
          </div>
        </AsyncBoundary>
      </section>

      <MerchPerformancePanel />

      {/* Online store sales */}
      <section className="rounded-xl border border-ovation-ink/10 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl">
              Online store sales
            </h2>
            <p className="mt-1 text-sm text-ovation-muted">
              Orders ingested via Shopify or WooCommerce webhooks.
            </p>
          </div>
          <Link
            to="/audience/map"
            className="inline-flex items-center gap-2 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm hover:bg-ovation-ink/5"
          >
            <MapPin className="h-3.5 w-3.5" />
            View on fan map
          </Link>
        </div>

        <AsyncBoundary isLoading={storeSummaryQ.isLoading} error={storeSummaryQ.error}>
          {summary && summary.totalOrders === 0 ? (
            <p className="mt-4 text-sm text-ovation-muted">
              No store orders yet. Connect Shopify or WooCommerce and configure webhooks.
            </p>
          ) : summary ? (
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-ovation-ink/10 bg-ovation-ink/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Total revenue
                </p>
                <p className="mt-1 text-2xl font-semibold text-ovation-ink">
                  {formatMoney(summary.totalRevenuePence)}
                </p>
              </div>
              <div className="rounded-lg border border-ovation-ink/10 bg-ovation-ink/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Orders
                </p>
                <p className="mt-1 text-2xl font-semibold text-ovation-ink">
                  {summary.totalOrders.toLocaleString()}
                </p>
              </div>
              <div className="rounded-lg border border-ovation-ink/10 bg-ovation-ink/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-ovation-muted">
                  Top country
                </p>
                <p className="mt-1 text-2xl font-semibold text-ovation-ink">
                  {summary.revenueByCountry[0]?.countryCode ?? '—'}
                </p>
              </div>
            </div>
          ) : null}
        </AsyncBoundary>

        <AsyncBoundary isLoading={intelligenceQ.isLoading} error={intelligenceQ.error}>
          {intel && intel.ordersAnalyzed > 0 && (
            <div className="mt-8 space-y-6 border-t border-ovation-ink/10 pt-6">
              <div>
                <h3 className="text-sm font-semibold text-ovation-ink">
                  What fans buy & where orders ship
                </h3>
                <p className="mt-1 text-xs text-ovation-muted">
                  Based on {intel.ordersAnalyzed} orders ({intel.itemsAnalyzed} units).
                </p>
              </div>
              {intel.insights.length > 0 && (
                <ul className="space-y-2">
                  {intel.insights.map((insight, i) => (
                    <li
                      key={i}
                      className="rounded-lg border border-amber-200/80 bg-amber-50 px-3 py-2 text-sm text-amber-950"
                    >
                      {insight.message}
                    </li>
                  ))}
                </ul>
              )}
              {intel.globalProductMix.length > 0 && (
                <div className="space-y-2">
                  {intel.globalProductMix.map((row) => (
                    <MixBar key={row.category} row={row} />
                  ))}
                </div>
              )}
            </div>
          )}
        </AsyncBoundary>

        <AsyncBoundary isLoading={storeOrdersQ.isLoading} error={storeOrdersQ.error}>
          {(storeOrdersQ.data?.length ?? 0) > 0 && (
            <div className="mt-6">
              <h3 className="mb-3 text-sm font-semibold text-ovation-ink">Recent store orders</h3>
              <DataTable
                rows={storeOrdersQ.data ?? []}
                rowKey={(o) => o.id}
                emptyMessage="No store orders."
                columns={[
                  {
                    key: 'orderedAt',
                    header: 'Date',
                    render: (o) => new Date(o.orderedAt).toLocaleDateString(),
                  },
                  {
                    key: 'source',
                    header: 'Store',
                    render: (o) => (o.source === 'shopify' ? 'Shopify' : 'WooCommerce'),
                  },
                  {
                    key: 'order',
                    header: 'Order',
                    render: (o) => o.orderNumber ?? o.externalOrderId,
                  },
                  {
                    key: 'location',
                    header: 'Ship to',
                    render: (o) => {
                      const parts = [o.city, o.region, o.countryCode].filter(Boolean)
                      return parts.length > 0 ? parts.join(', ') : '—'
                    },
                  },
                  {
                    key: 'total',
                    header: 'Total',
                    align: 'right',
                    render: (o) => formatMoney(o.totalPence, o.currency),
                  },
                ]}
              />
            </div>
          )}
        </AsyncBoundary>
      </section>
    </div>
  )
}

function MixBar({ row }: { row: ProductMixRow }) {
  const pct = Math.round(row.share * 100)
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-36 shrink-0 text-ovation-muted">{row.label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-ovation-ink/10">
        <div
          className="h-full rounded-full bg-orange-500/80"
          style={{ width: `${Math.max(4, pct)}%` }}
        />
      </div>
      <span className="shrink-0 tabular-nums text-ovation-muted">
        {row.quantity} · {pct}%
      </span>
    </div>
  )
}
